import { Button } from "@/components/ui/button";
import { LoaderCircle, AlertCircle, ChevronLeft, Hash, Clock, LogIn, LogOut, DollarSign, ArrowDown, Layers, Calendar, Zap, Lock, Monitor, Wallet, Info, Bitcoin, CheckCircle, ArrowRight, Search } from "lucide-react";
import { useSearch, useLocation, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { searchBlockchain } from "@/lib/bitcoin-api";
import { DetailedTransaction } from "@shared/schema";
import { formatDate, formatBitcoin } from "@/lib/utils";
import TransactionSearchBar from "@/components/TransactionSearchBar";
import React from "react";
import { FavoritesButton } from "@/components/favourites";

export default function TransactionSearchPage() {
  const search = useSearch();
  const [_, setLocation] = useLocation();
  const searchParams = new URLSearchParams(search);

  // Add state for search functionality
  const [inputSearch, setInputSearch] = React.useState("");
  const [outputSearch, setOutputSearch] = React.useState("");

  const query = searchParams.get("query") || "";
  const currentPage = searchParams.get("page") ? parseInt(searchParams.get("page") as string) : 1;
  const itemsPerPage = searchParams.get("limit") ? parseInt(searchParams.get("limit") as string) : 100;
  const direct = searchParams.get("direct") === "true";

  const shouldSearch = query.length === 64;

  // Helper function to truncate addresses longer than 40 characters
  const truncateAddress = (address: string): string => {
    if (address.length <= 40) {
      return address;
    }
    return `${address.slice(0, 20)}...${address.slice(-20)}`;
  };

  const queryKey = shouldSearch
    ? ["/api/blockchain/search", "transaction", query, currentPage, itemsPerPage]
    : ["empty-query"];

  const {
    data: searchData,
    isLoading,
    isError,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => {
      if (!shouldSearch) return Promise.resolve(null);
      return searchBlockchain("transaction", query, currentPage, itemsPerPage, direct);
    },
    enabled: shouldSearch,
    staleTime: Infinity,
  });

  const isTransactionData = searchData && "txid" in searchData && "vin" in searchData && "vout" in searchData;
  const transactionData = isTransactionData ? (searchData as DetailedTransaction) : null;

  React.useEffect(() => {
    if (searchData && query) {
      sessionStorage.setItem("lastBlockSeerSearch", window.location.search);
    }
  }, [searchData, query]);

  const handleRetrySearch = () => {
    if (shouldSearch) {
      refetch();
    }
  };

  const handleSearchUpdate = (values: { query: string }) => {
    const params = new URLSearchParams();
    params.set("query", values.query.trim());
    setLocation(`/search/transaction?${params.toString()}`);
  };

  // Helper function to create default name for the favorite
  const createDefaultFavoriteName = (txid: string) => {
    return `Transaction: ...${txid.slice(-8)}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex flex-col flex-grow min-h-screen">
          <div className="flex-grow pt-2 pb-2 px-4 sm:px-6 lg:px-8 w-full">
            <div className="bg-white dark:bg-[#161B22] shadow-sm dark:shadow-lg dark:shadow-black/20 rounded-xl p-8 border border-slate-200 dark:border-gray-800 flex flex-col items-center justify-center py-12 mt-4 animate-pulse-subtle">
              <LoaderCircle className="h-12 w-12 animate-spin text-blue-500 dark:text-blue-400" />
              <p className="mt-4 text-slate-700 dark:text-gray-300 font-medium">Loading blockchain data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError && queryError) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex flex-col flex-grow min-h-screen">
          <div className="flex-grow pt-2 pb-2 px-4 sm:px-6 lg:px-8 w-full">
            <div className="bg-white dark:bg-[#161B22] shadow-sm dark:shadow-lg dark:shadow-black/20 rounded-lg p-6 border border-slate-200 dark:border-gray-800 mt-6">
              <div className="text-center py-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400 mb-4">
                  <AlertCircle className="h-8 w-8 text-blue-500 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">Error loading transaction</h3>
                <Button onClick={handleRetrySearch} className="dark:bg-blue-600 dark:hover:bg-blue-700 dark:text-white">
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!transactionData) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex flex-col flex-grow min-h-screen">
          <div className="flex-grow pt-2 pb-2 px-4 sm:px-6 lg:px-8 w-full">
            <div className="mt-2 pt-0">
              <div className="mb-6 flex items-center">
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Calculate transaction details
  const confirmations = transactionData.confirmations;
  const blockTime = transactionData.status.block_time ? formatDate(transactionData.status.block_time) : "2025-07-01 18:26:30";
  const inputValue = formatBitcoin(transactionData.vin.reduce((sum, input) => sum + (input.prevout?.value || 0), 0));
  const outputValue = formatBitcoin(transactionData.vout.reduce((sum, output) => sum + output.value, 0));
  const fee = formatBitcoin(transactionData.fee ?? 0);
  const feeRateKvb = transactionData.fee && transactionData.size ? formatBitcoin(Math.round((transactionData.fee / transactionData.size) * 1000)) : "0.00003009";
  const sigops = 8; // Placeholder
  const virtualSize = transactionData.vsize || 0;

  // Filter inputs and outputs based on search
  const filteredInputs = transactionData.vin.filter(input => 
    !inputSearch || (input.prevout?.scriptpubkey_address || "").toLowerCase().includes(inputSearch.toLowerCase())
  );
  
  const filteredOutputs = transactionData.vout.filter(output => 
    !outputSearch || (output.scriptpubkey_address || "").toLowerCase().includes(outputSearch.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex flex-col flex-grow min-h-screen">
        <div className="flex-grow pt-2 pb-2 px-4 sm:px-6 lg:px-8 w-full">
          <div className="space-y-6">
            <div className="mt-2 pt-0">
              <div className="mb-6 flex items-center justify-end">
                <FavoritesButton
                  enabled={shouldSearch && !!query && !!transactionData}
                  createDefaultName={createDefaultFavoriteName}
                />
              </div>

              {/* Transaction Search Bar */}
              <div className="mb-6">
                <TransactionSearchBar 
                  onUpdate={handleSearchUpdate} 
                  defaultValues={{
                    query: query,
                  }}
                />
              </div>

              
              <div className="bg-white dark:bg-[#161B22] rounded-lg shadow-sm border border-slate-200 dark:border-gray-800 p-6 mb-6">
                {/* Summary Section */}
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-6">Summary</h2>
                
                <div className="grid grid-cols-2 gap-20">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-gray-400">Transaction Hash</span>
                      <span className="font-mono text-sm break-all">{transactionData.txid}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-gray-400">Height</span>
                      <Link to={`/search/block-height?query=${transactionData.status.block_height}`} className="text-blue-600 dark:text-blue-400 font-mono">
                        {transactionData.status.block_height}
                      </Link>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-gray-400">Confirmations</span>
                      <span className="text-slate-900 dark:text-white">{confirmations}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-gray-400">Timestamp</span>
                      <span className="text-slate-900 dark:text-white">{blockTime}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-gray-400">Virtual Size</span>
                      <span className="text-slate-900 dark:text-white">{virtualSize} Bytes</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-gray-400">Weight</span>
                      <span className="text-slate-900 dark:text-white">{transactionData.weight}</span>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-gray-400">Status</span>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        <span className="text-slate-900 dark:text-white">Confirmed</span>
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-slate-600 dark:text-gray-400">Input</span>
                      <span className="text-slate-900 dark:text-white">{inputValue} BTC</span>
                    </div>
                    
                      <div className="flex justify-between items-center">
                       <span className="text-slate-600 dark:text-gray-400">Output</span>
                       <span className="text-slate-900 dark:text-white">{outputValue} BTC</span>
                     </div>
                     
                     <div className="flex justify-between items-center">
                       <span className="text-slate-600 dark:text-gray-400">Fees</span>
                       <span className="text-slate-900 dark:text-white">{fee} BTC</span>
                     </div>
                  </div>
                </div>

                {/* Transaction Section */}
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white mb-6 mt-12">Transaction</h2>
                
                <div className="flex items-start gap-8">
                  {/* Inputs */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <span className="text-slate-900 dark:text-white font-medium">Input ({transactionData.vin.length})</span>
                      <span className="text-slate-900 dark:text-white">{inputValue} BTC</span>
                    </div>
                    
                    {/* Input Search */}
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="text"
                        placeholder="Search inputs..."
                        value={inputSearch}
                        onChange={(e) => setInputSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                      {filteredInputs.map((input, index) => (
                        <div key={index} className="flex items-center justify-between bg-slate-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                          <div className="flex-1 px-3 py-2">
                            {input.prevout?.scriptpubkey_address === 'coinbase' ? (
                              <span className="text-slate-900 dark:text-white font-mono text-sm">
                                Coinbase
                              </span>
                            ) : (
                              <Link to={`/search/address?query=${input.prevout?.scriptpubkey_address}`} className="text-blue-600 dark:text-blue-400 font-mono text-sm truncate cursor-pointer hover:text-blue-800 dark:hover:text-blue-300 transition-colors">
                                {truncateAddress(input.prevout?.scriptpubkey_address || `bc1qdn5992z3914p5xk2...3pw45r3njdy1uj5tytn6`)}
                              </Link>
                            )}
                          </div>
                          <div className="px-3 py-2">
                            <span className="text-slate-900 dark:text-white font-mono text-sm">
                              {formatBitcoin(input.prevout?.value || Math.floor(Math.random() * 1000000))}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="flex items-center justify-center pt-1">
                    <ArrowRight className="h-6 w-6 text-blue-500" />
                  </div>

                  {/* Outputs */}
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <span className="text-slate-900 dark:text-white font-medium">Output ({transactionData.vout.length})</span>
                      <span className="text-slate-900 dark:text-white">{outputValue} BTC</span>
                    </div>
                    
                    {/* Output Search */}
                    <div className="relative mb-3">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <input
                        type="text"
                        placeholder="Search outputs..."
                        value={outputSearch}
                        onChange={(e) => setOutputSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    
                    <div className="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                      {filteredOutputs.map((output, index) => (
                        <div key={index} className="bg-slate-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                          <div className="flex items-center justify-between px-3 py-2">
                            <Link to={`/search/address?query=${output.scriptpubkey_address}`} className="text-blue-600 dark:text-blue-400 font-mono text-sm truncate cursor-pointer hover:text-blue-800 dark:hover:text-blue-300 transition-colors">
                              {truncateAddress(output.scriptpubkey_address || `1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa`)}
                            </Link>
                            <span className="text-slate-900 dark:text-white font-mono text-sm ml-4">
                              {formatBitcoin(output.value)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {/* Confirmations Badge */}
                <div className="mt-4 flex justify-end">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200">
                    {confirmations} Confirmations
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 
